import Vue from 'vue';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FTProcess } from './FTProcess';
import _ from 'lodash';
import store from '../store';
import { FTProcessRowDimensions } from './FTProcessRowDimensions';
import { GoodsObject } from './GoodsObject';
import { FTFactoryTicket } from './FTFactoryTicket';
import { HashTable } from './HashTable';
import { processHelpers } from './../components/page-items/custom/CFtProcesses/index';

export enum FTProcessRowTypes {
  'input',
  'output'
}

interface GoodsType {
  name: string;
}

export const GoodsTypes: HashTable<GoodsType> = {
  'front-rm': {
    name: 'Front Raw Material'
  },
  'back-rm': {
    name: 'Back Raw Material'
  },
  'packing-material': {
    name: 'Packing Material'
  },
  'coating-material': {
    name: 'Coating Material'
  },
  'sheet': {
    name: 'Sheet'
  },
  'adhesive': {
    name: 'Adhesive'
  },
  'layer': {
    name: 'Layer'
  },
  'ink': {
    name: 'Ink'
  },
  'coating-layer': {
    name: 'Coating Layer'
  },
  'laminated-material': {
    name: 'Laminated Material'
  },
  'lamination': {
    name: 'Lamination'
  },
  'wet-wipe': {
    name: 'Wet Wipes'
  },
  'fragrance': {
    name: 'Fragrance'
  },
  'product': {
    name: 'Product'
  },
  'liner-input': {
    name: 'Liner Input'
  },
  'corrugated-input': {
    name: 'Corrugated Input'
  },
  'ethanol': {
    name: 'Ethanol'
  }
}

export class FTProcessRow {
  _id: string | number = '';
  childrenProcessRows: HashTable<FTProcessRow> = {};
  parentProcessRow: FTProcessRow | null = null;
  goodsType?: string = undefined;
  status?: string = undefined;
  parent = '';
  type = FTProcessRowTypes.input;
  quantity = 0;
  dimensions: FTProcessRowDimensions;
  ftProcess: FTProcess;
  processRowConfig: any = { params: {}};
  noOfPieces = 0;
  pcsPerSheet?: number = undefined;
  customerWaste?: number = undefined;
  layers = 0;
  key = -1;
  goodsObject$ = new Subject<GoodsObject>();
  id$ = new Subject<number | string>();
  noOfPieces$ = new Subject<number>();
  //@ts-ignore
  goodsObject: GoodsObject;
  goodsLabel?: string;
  quantity$ = new Subject<number>();
  groupedOutputParams: any[] = [];
  report?: { quantity: number, runningWaste: number, materialReturn: number, varience: number };
  remainingQuantity?: number;
  static quantityUpdateProps = {
    'noOfPieces': (instance: FTProcessRow, value: number) => {
      if (instance.goodsObject) {
        let perKgRatio = 0;
        if(_.get(instance, 'goodsObject.perKgRatio.plA')) {
          perKgRatio = instance.goodsObject.perKgRatio.plA.value;
        } else if (_.get(instance, 'goodsObject.perKgRatio.plA')) {
          perKgRatio = instance.goodsObject.perKgRatio.plA.value;
        }
        return value * perKgRatio;
      }
      return 0;
    }
  };
  static excludedFromJson = [
    'ftProcess',
    'processRowConfig',
    '__text',
    'key',
    'parentProcessRow',
    'childrenProcessRows',
    'id$',
    'goodsObject$',
    'noOfPieces$',
    'quantity$',
    'groupedOutputParams'
  ]
  static includedInJson = ['_id']
  

  constructor({ config = {}, ftProcessRow, ftProcess} : { config?: any, ftProcessRow?: FTProcessRow, ftProcess: FTProcess }) {
    this._id = config._id;  // has to be set before config merge to define goodsObject
    _.merge(this, config);
    this.dimensions = config.dimensions ? config.dimensions : {};
    this.ftProcess = ftProcess;
    if (ftProcessRow) {
      _.merge(this, ftProcessRow);
    }
    if (this.ftProcess) {
      if(this.type === FTProcessRowTypes.input) {
        const goodsKey : number = _.findIndex(this.ftProcess.config.inputs.goods, { goodsType : this.goodsType });
        this.processRowConfig = goodsKey > -1 ? processHelpers[this.ftProcess.type].loadConfig({ process: this.ftProcess, processRow: ftProcessRow }).inputs.goods[goodsKey]: {};
      } else {
        this.processRowConfig = processHelpers[this.ftProcess.type].loadConfig({ process: this.ftProcess, processRow: ftProcessRow }).outputs;
      }
    }
    if (this.type === FTProcessRowTypes.output) {
      this._id = config._id ? config._id : `p${ftProcess.index+1}o${this.key+1}`;
      const status = ['Packing', 'Trading'].includes(ftProcess.type) ? 'FG' : 'WIP';
      const newOutputGoodsObject: GoodsObject = new GoodsObject({ _id: this._id, status }, this )
      store.commit('updateSingleGood', newOutputGoodsObject);
    }

    this.id$.pipe(
      tap((id: number | string) => {
        this.setParentProcessRow(id);
      }),
      tap((id: number | string) => {
        this.updateGoodsObject(id);
      })
    ).subscribe();
    
    this.goodsObject$.pipe(
      tap(goodsObject => {
        this.goodsObject = goodsObject;
        setTimeout(() => {
          this.noOfPieces = FTProcessRow.quantityUpdateProps.noOfPieces(this, this.quantity);
        }, 0);
      })
    ).subscribe();

    this.id$.next(this._id);
    this.noOfPieces$.pipe(
      tap((noOfPieces: number) => {
        if(!isNaN(_.get(this, 'goodsObject.perKgRatio.plA.value'))) {
          const newQuantity = noOfPieces / this.goodsObject.perKgRatio.plA.value;
          if(this.quantity !== newQuantity) {
            this.quantity = newQuantity;
            this.quantity$.next(this.quantity);
          }
        }
        this.noOfPieces = noOfPieces;
      })
    ).subscribe();
    this.goodsLabel = this.getGoodsLabel();

    this.quantity$.pipe(
      tap((quantity: number) => {
        _.forEach(FTProcessRow.quantityUpdateProps, (updateFn: Function, key: string) => {
          const newValue = updateFn(this, quantity);
          // @ts-ignore
          if(newValue !== this[key]) {
            // @ts-ignore
            this[key] = updateFn(this, quantity);
          }
        })
      })
    ).subscribe();
    this.quantity$.next(this.quantity);
  }

  updateConfig() {
    if(this.type === FTProcessRowTypes.input) {
      const goodsKey : number = _.findIndex(this.ftProcess.config.inputs.goods, { goodsType : this.goodsType });
      this.processRowConfig = goodsKey > -1 ? processHelpers[this.ftProcess.type].loadConfig({ process: this.ftProcess, processRow: this }).inputs.goods[goodsKey]: {};
    } else {
      this.processRowConfig = processHelpers[this.ftProcess.type].loadConfig({ process: this.ftProcess, processRow: this }).outputs;
      this.groupedOutputParams = this.getGroupedOutputParams();
    }
  }

  getGroupedOutputParams(): any[] {
    const [filteredProcessOutputParams, filteredProcessOutputParamsGroups] = _.partition(this.processRowConfig.arguments, (outputParam: any) => {
      return !outputParam.group
    });
    const resolvedOutputParams = [ ...filteredProcessOutputParams, { type: 'group', groupItems: filteredProcessOutputParamsGroups } ];
    return resolvedOutputParams;
  }

  setParentProcessRow(id: number | string) {
    if (FTFactoryTicket.isWithinFT(id)) {
      if(this.type === FTProcessRowTypes.input) {
        this.parent = `${id}`;
      } else {
        this.parent = `p${this.ftProcess.index+1}i1`;
      }
      if (this.ftProcess && _.includes(this.ftProcess.ftFactoryTicket.processes, this.ftProcess) && this.parent) {
        this.parentProcessRow = FTFactoryTicket.getFTProcessRow(this.ftProcess.ftFactoryTicket.processes, this.parent);
        this.parentProcessRow.childrenProcessRows[id] = this;
      }
    }
  }

  updateGoodsObject(id: number | string) {
    let goodsKey = id;
    if (FTFactoryTicket.isWithinFT(this._id) && !store.state.goods.idToIndex[goodsKey] && this.ftProcess.ftFactoryTicket.processes.length) {
      goodsKey = FTFactoryTicket.getFTProcessRow(this.ftProcess.ftFactoryTicket.processes, this.parent)._id;
    }
    this.goodsObject$.next(store.getters.getGoodFromId(goodsKey));
  }

  getGoodsLabel() {
    if (this.type === FTProcessRowTypes.input && this.goodsType === '') {
      return 'Input Goods'
    }
    if (this.type === FTProcessRowTypes.output) {
      return `Output Goods ${this.key+1}`
    }
    if (this.goodsType) {
      if (GoodsTypes[this.goodsType]) {
        return GoodsTypes[this.goodsType].name;
      } else {
        throw new Error(`Cannot find GoodsType for ${this.goodsType}`)
      }
    }
    return this.goodsType;
  }

  get packingMode(): { unit1: string, unit2: string, quantity: number } | undefined {
    const goodsKey = _.findLastKey(this.ftProcess.inputs, { goodsType : 'product' });
    if (!goodsKey) return undefined;
    return this.ftProcess.ftFactoryTicket.order.product.packingMode[this.key - parseInt(goodsKey) - 1];
  }

  toJSON() {
    const tempVariables = Object.keys(FTProcessRow.quantityUpdateProps);
    const variablesToSkip = [ ...FTProcessRow.excludedFromJson, ...tempVariables ];
    const returnedJSON = _.pickBy(this, (value, variableName) => !variablesToSkip.includes(variableName));
    Object.assign(returnedJSON, _.pick(this, FTProcessRow.includedInJson));
    return returnedJSON
  }

  onUserChangeRM() {
    this.id$.next(this._id);
    if(this.ftProcess.type === 'Packing') {
      this.fnGetDefaultNoOfPieces();
    }
  }

  onUserChangeProcessRowParam(paramName: string) {
    if(paramName === 'noOfPieces') {
      this.noOfPieces$.next(this.noOfPieces);
    }
    if(paramName === 'quantity') {
      this.quantity$.next(this.quantity);
    }
  }

  showRemoveInputIcon(): boolean {
    const hideRemoveInputIcon = !!this.processRowConfig.hideRemoveInputIcon;
    if(hideRemoveInputIcon) {
      return !hideRemoveInputIcon;
    } else {
      return this.key !== _.findIndex(this.ftProcess.inputs, { goodsType: this.goodsType });

    }
  }

  showRemoveOutputIcon(): boolean {
    const hideRemoveOutputIcon = !!this.processRowConfig.hideRemoveOutputIcon;
    if(hideRemoveOutputIcon) {
      return !hideRemoveOutputIcon;
    } else {
      return this.key > 0;

    }
  }

  fnGetDefaultNoOfPieces() {
    const packingMaterialStartingKey = _.findKey(this.ftProcess.inputs, { goodsType: 'packing-material'});
    let remainingNumberOfPieces = 0;
    _.forEach(this.ftProcess.inputs, (input: FTProcessRow) => {
      if(input.goodsType !== 'packing-material') {
        remainingNumberOfPieces = remainingNumberOfPieces + input.noOfPieces;
      }
    });
    if(remainingNumberOfPieces && packingMaterialStartingKey) {
      this.ftProcess.ftFactoryTicket.order.product.packingMode.forEach((packingModeItem: any, index: number) => {
        remainingNumberOfPieces = Math.floor(remainingNumberOfPieces/packingModeItem.quantity);
        const currentFTProcessRow = this.ftProcess.inputs[parseInt(packingMaterialStartingKey) + index];
        if(!currentFTProcessRow.noOfPieces && currentFTProcessRow.goodsObject) {
          currentFTProcessRow.noOfPieces$.next(remainingNumberOfPieces);
        }
      })
    }
  }

  remove() {
    const siblings = this.type === FTProcessRowTypes.input ? this.ftProcess.inputs : this.ftProcess.outputs;
    Vue.delete(siblings, this.key);
    _.forEach(siblings, (ftProcessRow: FTProcessRow) => {
      if(ftProcessRow.key > this.key) {
        ftProcessRow.key--;
        siblings[ftProcessRow.key] = ftProcessRow;
        Vue.delete(siblings, ftProcessRow.key + 1);
        if (this.type === FTProcessRowTypes.output) {
          ftProcessRow.relinkChildren();
        } 
      }
    })
  }

  relinkChildren() {
    const newOutputId = `p${this.ftProcess.index+1}o${this.key+1}`;
    _.mapValues(this.childrenProcessRows, (childrenProcessRow: FTProcessRow) => {
      childrenProcessRow._id = newOutputId;
    })
    this._id = newOutputId;
  }

}